import { useCallback, useMemo } from 'react';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useQueryClient } from 'react-query';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useIncomingCallNotification } from '@frontend/notifications';
import { CallDetailsContext } from '@frontend/phone-panels';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useVoiceAIRCustomizationFlag } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';
import { useAlert } from '@frontend/design-system';

export const useWebsocketMissedCallEventSubscription = () => {
  const queryClient = useQueryClient();
  const alert = useAlert();
  const { setShow } = useSlidePanelShallowStore<CallDetailsContext>('setShow');
  const { selectedLocationIds } = useAppScopeStore();

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isMissedCallNotificationFeatureEnabled = getFeatureFlagValue('missed-call-notification-feature-enabled');

  const hasAccesstoCallRecordingMap = useMemo(() => {
    return selectedLocationIds.reduce((accessMap, locationId) => {
      accessMap[locationId] = hasSchemaACL(locationId, Permission.CALL_RECORDING_READ);
      return accessMap;
    }, {} as Record<string, boolean>);
  }, [selectedLocationIds]);

  const hasAccesstoCallRecording = useMemo(() => {
    return Object.values(hasAccesstoCallRecordingMap).some((hasAccess) => hasAccess);
  }, [hasAccesstoCallRecordingMap]);

  const { locationFlagValueMap } = useVoiceAIRCustomizationFlag(selectedLocationIds);

  const { create: createNotification } = useIncomingCallNotification({
    onView: async (event) => {
      const { payload: params } = event;

      try {
        const callRecordData = await queryClient.fetchQuery({
          queryKey: [params.locationId, params.callRecordId],
          queryFn: () =>
            PhoneCallsApi.getCallRecord({
              callRecordId: params.callRecordId,
              channelId: params.channelId,
              locationId: params.locationId,
              startTime: params.startedAt,
              endTime: params.startedAt,
              includeCallRecording: true,
              hydrationFlags: {
                includeCallRecording: true,
                includePersonInfo: true,
                includePhoneTreeData: true,
                includeUserInfo: true,
                includeAiCallCreditsDetail: locationFlagValueMap[params.locationId] ?? false,
              },
            }),
          staleTime: 0,
        });
        setShow(true, 'callDetails', {
          rows: callRecordData.record ? [{ ...callRecordData.record, tags: [] }] : [],
          currentRowId: callRecordData?.record?.id ?? '',
          hasAccessToRecordings: hasAccesstoCallRecording,
          toggleRowSelected: () => {},
          callDetailsQueryKey: [],
          sectionFocus: 'details',
        });
      } catch (error) {
        alert.error('Error fetching call record data');
      }
    },
  });

  const eventHandler = useCallback<GetWebsocketEventHandler<NotificationType.MISSED_CALL>>(
    (event) => {
      const { params } = event;

      if (!isMissedCallNotificationFeatureEnabled) {
        return;
      }
      createNotification({
        id: event.id,
        timestamp: Date.now(),
        type: 'missed-call',
        payload: params,
        state: {
          paused: false,
          status: 'unread',
        },
      });
    },
    [createNotification]
  );
  return useWebsocketEventSubscription(NotificationType.MISSED_CALL, eventHandler);
};
